import { useMemo } from "react"
import { ProductMediaPnLRow } from "../connect"
import { TableVisualization } from "~/ui-rtk/components/ui/charts"
import {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import { PaginationState } from "@tanstack/react-table"
import { Loader } from "~/ui-rtk/components/ui/common"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

const CHART_ID = "product-media-pnl-table"

type TableRow = ProductMediaPnLRow

const formatNumber = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 0 })

const formatCurrency = (value: number) =>
  `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

function ProductTitleCell({ row }: { row: TableRow }) {
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const hasValidTitle = Boolean(row.productTitle)
  return (
    <div className={cn("max-w-xs", isDemoEnabled && "blur-md")}>
      <div className="font-medium text-white truncate">
        {hasValidTitle ? row.productTitle : row.productHandle}
      </div>
      {hasValidTitle && (
        <div className="text-xs text-white/50 truncate">
          {row.productHandle}
        </div>
      )}
    </div>
  )
}

const numberCell = (key: keyof TableRow) => (row: TMetric) => {
  const typedRow = row as unknown as TableRow
  return (
    <div className="text-right font-medium">
      {formatNumber(typedRow[key] as number)}
    </div>
  )
}

const currencyCell = (key: keyof TableRow) => (row: TMetric) => {
  const typedRow = row as unknown as TableRow
  return (
    <div className="text-right font-medium">
      {formatCurrency(typedRow[key] as number)}
    </div>
  )
}

export const ALL_COLUMNS: TableVisualizationColumn[] = [
  {
    key: "productHandle",
    header: "Handle",
    type: "text",
    disabled: true,
  },
  {
    key: "productTitle",
    header: "Product",
    type: "text",
    width: 280,
    render: (row: TMetric) => (
      <ProductTitleCell row={row as unknown as TableRow} />
    ),
  },
  // --- Ad exposure metrics (non-additive across products) ---
  {
    key: "activeAds",
    header: "Active Ads",
    type: "number",
    width: 120,
    render: numberCell("activeAds"),
  },
  {
    key: "totalSpent",
    header: "Total Spent",
    type: "currency",
    width: 140,
    render: currencyCell("totalSpent"),
  },
  {
    key: "modeledRevenue",
    header: "Modeled Revenue",
    type: "currency",
    width: 160,
    render: currencyCell("modeledRevenue"),
  },
  {
    key: "platformRevenue",
    header: "Platform Revenue",
    type: "currency",
    width: 160,
    render: currencyCell("platformRevenue"),
  },
  {
    key: "brandValue",
    header: "Brand Value",
    type: "currency",
    width: 140,
    render: currencyCell("brandValue"),
  },
  {
    key: "modeledBrandValue",
    header: "Modeled Brand Value",
    type: "currency",
    width: 180,
    render: currencyCell("modeledBrandValue"),
  },
  // --- Sales metrics ---
  {
    key: "totalRevenue",
    header: "Total Revenue",
    type: "currency",
    width: 150,
    render: currencyCell("totalRevenue"),
  },
  {
    key: "newCustomerRevenue",
    header: "New-Customer Revenue",
    type: "currency",
    width: 200,
    render: currencyCell("newCustomerRevenue"),
  },
  {
    key: "firstOrders",
    header: "First Orders",
    type: "number",
    width: 130,
    render: numberCell("firstOrders"),
  },
  {
    key: "sessions",
    header: "Sessions",
    type: "number",
    width: 120,
    render: numberCell("sessions"),
  },
]

interface ProductMediaPnLTableProps {
  data: ProductMediaPnLRow[]
  isLoading: boolean
  columnConfig?: TableVisualizationColumn[]
}

// TableVisualization requires a paginationState prop even when it manages
// pagination internally (enablePagination=false). It is ignored in that mode,
// so a stable static value is passed.
const UNUSED_PAGINATION_STATE: PaginationState = { pageIndex: 0, pageSize: 100 }

export default function ProductMediaPnLTable({
  data,
  isLoading,
  columnConfig: columnConfigProp,
}: ProductMediaPnLTableProps) {
  const metrics = useMemo(
    () =>
      data.map(row => ({
        ...row,
        id: row.productHandle,
      })),
    [data],
  )

  const columnConfig = useMemo<TableVisualizationColumn[]>(
    () => columnConfigProp ?? ALL_COLUMNS,
    [columnConfigProp],
  )

  if (!data.length && !isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-system-gray-400">
        No data available for the selected filters.
      </div>
    )
  }

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-basic-black/50 rounded-system-lg">
          <Loader size={50} />
        </div>
      )}
      <TableVisualization
        metrics={metrics as TMetric[]}
        columnConfig={columnConfig}
        paginationState={UNUSED_PAGINATION_STATE}
        enablePagination={false}
        chartId={CHART_ID}
        compareUniqueKey="productHandle"
      />
    </div>
  )
}
